import React, { useEffect, useRef } from 'react';

import { LineChart } from './charts';
import { BusBadge, fmt, Kpi, Note, Pill, Row } from './parts';
import { BUS_COLOR, clock, feederDetail, feederTrend } from './model';

/* Modal plumbing shared by the feeder panel and the profile dialog: focus moves
   in on open and back to whatever opened it on close, Tab stays inside, Escape
   closes, and the page behind stops scrolling. */
const useModal = onClose => {
    const closeRef = useRef(null);

    useEffect(() => {
        const opener = document.activeElement;
        const onKey = event => {
            if (event.key === 'Escape') onClose();
            if (event.key !== 'Tab' || !closeRef.current) return;

            const box = closeRef.current.closest('[role="dialog"]');
            const stops = box.querySelectorAll('button:not(:disabled), a[href], input, select, [tabindex="0"]');
            const first = stops[0];
            const last = stops[stops.length - 1];

            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };

        if (closeRef.current) closeRef.current.focus();
        document.addEventListener('keydown', onKey);

        const overflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = overflow;
            if (opener && opener.focus) opener.focus();
        };
    }, [onClose]);

    return closeRef;
};

export function FeederDrawer({ sim, feederId, onClose }) {
    const closeRef = useModal(onClose);
    const { dc, now, acks, acknowledge } = sim;
    const feeder = feederDetail(dc, feederId);

    if (!feeder) return null;

    const { condition, stale } = feeder;
    const trend = feederTrend(feeder, dc);
    const trendTicks = [0, 0.5, 1].map(f => trend[Math.round((trend.length - 1) * f)].t);
    const thermal = condition && condition.kind === 'thermal';

    return (
        <div className="dash-scrim" onClick={onClose}>
            <aside
                className="dash-drawer"
                role="dialog"
                aria-modal="true"
                aria-labelledby="dash-drawer-title"
                onClick={event => event.stopPropagation()}
            >
                <header className="dash-drawer__head">
                    <BusBadge bus={feeder.bus} />
                    <div>
                        <h2 id="dash-drawer-title">Feeder {feeder.id}</h2>
                        <p>{feeder.location} · Joint {feeder.joint}</p>
                    </div>
                    <button type="button" ref={closeRef} className="dash-close" onClick={onClose} aria-label="Close feeder details">×</button>
                </header>

                <div className="dash-drawer__body">
                    <div className="dash-drawer__status">
                        {thermal && <Pill tone={condition.severity === 'critical' ? 'crit' : 'warn'}>{condition.severity === 'critical' ? 'Critical temperature' : 'Thermal advisory'}</Pill>}
                        {stale && <Pill>Temperature unknown</Pill>}
                        {!condition && <Pill tone="ok">Within limits</Pill>}
                    </div>

                    {condition && <p className="dash-callout">{condition.body}</p>}

                    <div className="dash-drawer__kpis">
                        <Kpi label="Feeder voltage" value={fmt(feeder.volts)} unit="VDC" note="Pole-to-pole" />
                        <Kpi label="Current" value={fmt(feeder.amps)} unit="A" note={`${fmt(feeder.loading)}% of 800 A`} />
                        <Kpi label="DC output power" value={fmt(feeder.kw, 2)} unit="kW" note="Steady-state V × I" />
                        <Kpi label="Joint temperature" value={stale ? '—' : fmt(feeder.temp)} unit={stale ? '' : '°C'} note={stale ? 'Last sample over 5 minutes old' : 'Positive tap-off termination'} />
                    </div>

                    <h3 className="dash-eyebrow">Last 6 hours</h3>
                    <LineChart
                        series={[{ id: 'load', label: 'Load current', color: BUS_COLOR[feeder.bus], area: true, points: trend.map(p => [p.t, p.amps]) }]}
                        height={120}
                        label={`Load current on feeder ${feeder.id} over the last 6 hours`}
                        xTicks={trendTicks}
                        formatX={clock}
                        formatY={value => Math.round(value)}
                        formatTip={value => `${fmt(value)} A`}
                        tipTitle={value => `${clock(value)} ET`}
                        yLabel="Load, A"
                    />
                    {stale ? (
                        <Note>No temperature trend: the last valid sample is more than 5 minutes old.</Note>
                    ) : (
                        <LineChart
                            series={[{ id: 'temp', label: 'Joint temperature', color: BUS_COLOR[feeder.bus], area: true, points: trend.map(p => [p.t, p.temp]) }]}
                            height={120}
                            label={`Joint temperature at ${feeder.joint} over the last 6 hours`}
                            xTicks={trendTicks}
                            formatX={clock}
                            formatY={value => `${value}`}
                            formatTip={value => `${fmt(value)} °C`}
                            tipTitle={value => `${clock(value)} ET`}
                            yLabel="Joint, °C"
                        />
                    )}

                    <h3 className="dash-eyebrow">Connection diagnostics</h3>
                    <Row label="Local ambient">{fmt(feeder.ambient)} °C</Row>
                    <Row label="Temperature rise">{stale ? '—' : `${fmt(feeder.rise)} K`}</Row>
                    <Row label="Temperature slope · last 5 min">{stale ? '—' : `${fmt(feeder.slope, 3)} K/min`}</Row>
                    <Row label="Time to 75 °C critical limit" note="At the present rate of rise, if nothing changes">
                        {feeder.toCritical === null ? 'Not heading there' : feeder.toCritical >= 60 ? `About ${fmt(feeder.toCritical / 60)} h` : `About ${fmt(feeder.toCritical, 0)} min`}
                    </Row>
                    <Row label="Instrumented joint drop">{fmt(feeder.dropMv)} mV</Row>
                    <Row label="Estimated joint resistance" note="Dedicated mV sensor ÷ current; valid above 50 A at stable load">{fmt(feeder.rUOhm)} µΩ</Row>
                    <Row label="Estimated joint loss" note="I²R steady-state estimate; not total feeder loss">{fmt(feeder.lossW, 2)} W</Row>
                    <Row label="Source-to-tap voltage drop">{fmt(feeder.pathDropV)} V / {fmt(feeder.pathDropPct, 3)}%</Row>

                    <h3 className="dash-eyebrow">Protection & redundancy</h3>
                    <Row label="Breaker state"><Pill tone={feeder.breaker === 'Closed' ? 'ok' : 'crit'}>{feeder.breaker}</Pill></Row>
                    <Row label="Trip indication">{feeder.breaker === 'Closed' ? 'No trip' : 'Upstream main open'}</Row>
                    <Row label="Paired alternate feed">{feeder.paired.id} · {fmt(feeder.paired.kw)} kW</Row>
                    <Row label="Modeled load after paired-feed loss" note="Constant-power estimate at present voltage; 800 A limit">{fmt(feeder.afterLoss)}%</Row>
                    <Row label="Interrupting rating / trip curve"><Pill>Not configured</Pill></Row>

                    {condition && (
                        <>
                            <h3 className="dash-eyebrow">{condition.severity === 'critical' ? 'Active alarm' : 'Active advisory'}</h3>
                            {acks[condition.id]
                                ? <Note>Acknowledged in this session. The condition stays active.</Note>
                                : <button type="button" className="dash-btn" onClick={() => acknowledge(condition.id)}>{condition.severity === 'critical' ? 'Acknowledge alarm' : 'Acknowledge advisory'}</button>}
                        </>
                    )}
                </div>

                <footer className="dash-drawer__foot">
                    Snapshot {clock(now)} ET · Simulated data · No remote switching controls
                </footer>
            </aside>
        </div>
    );
}

export function ProfileDialog({ sim, onClose }) {
    const closeRef = useModal(onClose);
    const { dc, ac } = sim;

    return (
        <div className="dash-scrim dash-scrim--center" onClick={onClose}>
            <div
                className="dash-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="dash-dialog-title"
                onClick={event => event.stopPropagation()}
            >
                <header className="dash-drawer__head">
                    <div>
                        <h2 id="dash-dialog-title">System profile</h2>
                        <p>The system behind every reading</p>
                    </div>
                    <button type="button" ref={closeRef} className="dash-close" onClick={onClose} aria-label="Close system profile">×</button>
                </header>

                <div className="dash-drawer__body">
                    <p className="dash-callout dash-callout--info">
                        <strong>Demo data.</strong> Values are simulated. No meters, protection devices, BMS or live data center are connected.
                    </p>

                    <Row label="AC distribution" note="Three-phase, four-wire wye; ten AC meter locations across A and B">{ac.profile.label}</Row>
                    <Row label="UPS operating mode" note="AC input, rectifier, DC link, inverter, AC distribution; bypass energized and unloaded">Double conversion</Row>
                    <Row label="UPS DC link" note="Two-wire +/− with PE; floating IT; no neutral">800 VDC pole-to-pole</Row>
                    <Row label="Distribution topology" note="Six inverter sections per path; no DC tie between A and B">Independent A / B</Row>
                    <Row label="Bus continuous limit" note="Allowable current; verify installation derating">4,000 A per bus</Row>
                    <Row label="Tap-off continuous limit" note="12 monitored feeders in 6 pairs">800 A per feeder</Row>
                    <Row label="Rectifier capacity" note="N+1 module allowance of 3.2 MW, also limited by bus ampacity">9 × 400 kW per path</Row>
                    <Row label="Worst surviving bus capacity" note="Lower present bus voltage × configured 4,000 A">{fmt(dc.capacityKw / 1000, 3)} MW</Row>
                    <Row label="Modeled surviving-path loading" note={`All present bus power on one path; highest feeder ${fmt(Math.max(...dc.pairLoading))}%`}>{fmt(dc.survivingUse)}%</Row>
                    <Row label="Hardware status" note="Interrupting and withstand ratings and trip curves not commissioned">Monitoring simulation only</Row>

                    <p className="dash-prose">The capacity model assumes constant present load, configured continuous limits, paired independent inverter sections, load support through the AC outputs and available sources. Transfer dynamics, added distribution loss, thermal derating and protection coordination need project-specific engineering.</p>
                    <p className="dash-prose">Displayed power uses V DC × I DC as a steady-state approximation. A production meter should integrate synchronous voltage and current samples, and fast disturbances need local waveform capture that a 2-second dashboard refresh cannot do.</p>
                </div>

                <footer className="dash-dialog__foot">
                    <button type="button" className="dash-btn dash-btn--primary" onClick={onClose}>Close profile</button>
                </footer>
            </div>
        </div>
    );
}

export function Toast({ message }) {
    return (
        <div className="dash-toast-slot" role="status" aria-live="polite">
            {message && <div className="dash-toast">{message}</div>}
        </div>
    );
}
